<!-- ══ Table & Matrix: config-driven type picker + per-type fields ══ -->
<div class="table-matrix-config" *ngIf="active">
  <div class="control-group">
    <label>{{ host.fieldLabel('tableMatrix', 'tableType', 'Table type') }}</label>
    <ng-select
      #ssTmType
      class="ss-select"
      [class.is-open]="!!ssTmType.isOpen?.()"
      appendTo="body"
      ngClass="ss-dropdown-panel"
      [items]="tableTypes"
      bindLabel="label"
      bindValue="value"
      groupBy="group"
      [clearable]="true"
      [ngModel]="tmType"
      (ngModelChange)="tmType = $event ?? ''; onTableMatrixTypeChange()"
    ></ng-select>
    <small class="control-hint" *ngIf="tmTypeHint">{{ tmTypeHint }}</small>
    <small class="control-hint tm-migration-notice" *ngIf="tmMigrationNotice">{{ tmMigrationNotice }}</small>
  </div>

  <!-- Dataset — declared (required) in charts/table-matrix.json's config
       section but previously never rendered in this form: the only picker
       lived in the collapsible target/data panel, so skipping that panel
       left Generate permanently disabled (and Drill-Down's pickers
       disabled with it). Single-select: the table renders from ONE
       dataset (drillDataset = first of selectedDatasets). -->
  <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

  <!-- Normal / Editable / Grouped / Tree / Financial: choose columns (empty = all).
       One row per column — each with its OWN dataset picker so a table can
       mix columns from different datasets ({dataset, field, displayName}
       in the compute payload). Dataset defaults to the primary one above. -->
  <!-- ALL flat table shapes share this block (per-type pickers + shared
       sections like Navigation live inside, each with its own gate).
       Previously gated to the five columns-based types only, which
       worked by ACCIDENT for Matrix/Pivot — the tmFlagsFor fallback
       co-stamped them `normal` — and hid their pickers once that flags
       bug was fixed. -->
  <ng-container *ngIf="isTmFlatShape">
    <!-- Normal / Editable / Grouped / Tree / Financial: choose columns
         (empty = all). Matrix/Pivot GENERATE their columns, so the
         column cards stay hidden for them. -->
    <!-- The column cards are a TEMPLATE so the header view toggle's second
         view gets the exact same per-column configuration (dataset, display
         name, editable + control, alignment, hidden, action columns,
         default filters). `columns` is the list being edited and `toggle`
         marks it as the second view's list. -->
    <ng-template #tmColumnCards let-columns="columns" let-toggle="toggle" let-label="label" let-hint="hint">
    <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onTmColumnFieldDrop($event, toggle)" title="Drag columns here (leave empty to show every column)">
      <label>{{ label }}</label>
      <small class="control-hint" *ngIf="!columns.length">
        {{ hint }}
      </small>

      <!-- The table shows the columns in this order: drag a card by its handle,
           or use the arrows, to move it. -->
      <div class="tm-column-list" *ngIf="columns.length"
           cdkDropList [cdkDropListData]="columns" cdkDropListLockAxis="y"
           (cdkDropListDropped)="onTmColumnDrop($event, toggle)">
        <div class="tm-column-card" *ngFor="let column of columns; let i = index; let first = first; let last = last" cdkDrag>
          <div class="tm-column-card-head">
            <span class="tm-drag-handle" cdkDragHandle matTooltip="Drag to reorder" aria-label="Drag to reorder">
              <mat-icon>drag_indicator</mat-icon>
            </span>
            <span class="wf-step-index">{{ i + 1 }}</span>
            <span class="tm-column-card-title">{{ column.displayName || column.field || (column.isAction ? 'Action column' : 'New column') }}</span>
            <button type="button" mat-icon-button class="tm-move-button" [disabled]="first" (click)="moveTmColumn(column, -1)" matTooltip="Move up" aria-label="Move column up">
              <mat-icon>arrow_upward</mat-icon>
            </button>
            <button type="button" mat-icon-button class="tm-move-button" [disabled]="last" (click)="moveTmColumn(column, 1)" matTooltip="Move down" aria-label="Move column down">
              <mat-icon>arrow_downward</mat-icon>
            </button>
            <button type="button" mat-icon-button class="row-remove-button" (click)="removeTmColumn(column)" matTooltip="Remove column" aria-label="Remove column">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="tm-column-card-body">
            <!-- An ACTION column binds to no dataset field, so its
                 Dataset / Column pickers are hidden: only the header text
                 and the Action settings below apply to it. -->
            <!-- A FORMULA column is computed per row from the other columns, so
                 it reads no field: its Dataset / Column pickers are hidden. -->
            <div class="control-group" *ngIf="!column.isAction">
              <label>Formula (optional)</label>
              <input
                type="text"
                [ngModel]="column.formula || ''"
                (ngModelChange)="onTmColumnFormulaChange(column, $event)"
                placeholder="e.g. ([set_price] - [target_price]) / [target_price] * 100"
              />
              <small class="control-hint">Computed per row from other columns, named in [brackets] by field or display name. Blank = show a dataset column.</small>
            </div>
            <div class="control-group" *ngIf="!column.isAction && !column.formula?.trim()">
              <label>Dataset</label>
              <ng-select
                #ssTmColDataset
                class="ss-select"
                [class.is-open]="!!ssTmColDataset.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="host.entityOptions"
                [loading]="host.entitiesLoading"
                [clearable]="true"
                [searchable]="true"
                placeholder="Same as primary"
                [ngModel]="column.dataset || host.drillDataset || null"
                (ngModelChange)="onTmColumnDatasetChange(column, $event)"
              ></ng-select>
            </div>
            <div class="control-group" *ngIf="!column.isAction && !column.formula?.trim()">
              <label>Column</label>
              <ng-select
                #ssTmColField
                class="ss-select"
                [class.is-open]="!!ssTmColField.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="columnsForTmColumn(column)"
                [clearable]="true"
                [searchable]="true"
                [loading]="isTmColumnLoading(column)"
                [placeholder]="tmColumnFieldPlaceholder(column)"
                [ngModel]="column.field || null"
                (ngModelChange)="onTmColumnFieldChange(column, $event)"
              ></ng-select>
            </div>
            <!-- A value inside a JSON column (e.g. approval_history → status). -->
            <div class="control-group" *ngIf="!column.isAction && !column.formula?.trim() && column.field">
              <label>Value inside JSON (optional)</label>
              <input
                type="text"
                [ngModel]="column.jsonPath || ''"
                (ngModelChange)="onTmColumnJsonPathChange(column, $event)"
                placeholder="e.g. status or approvalLevel.0.status"
              />
              <small class="control-hint">For a JSON column: the key to show, dot-separated for nested keys (a number picks an array item). Blank = the whole column.</small>
            </div>
            <div class="control-group">
              <label>{{ column.isAction ? 'Header label' : 'Display name' }}</label>
              <input
                type="text"
                [ngModel]="column.displayName"
                (ngModelChange)="onTmColumnDisplayNameChange(column, $event)"
                [placeholder]="column.isAction ? 'Optional — blank for a bare button column' : 'Header label'"
              />
            </div>
            <!-- NAVIGATION — its own accordion right under the name: click a
                 cell value → open a published dashboard filtered on that row
                 (or a route / URL). Stored on the column's cellConfig, like
                 the formatting below, and rendered by every table type. -->
            <div class="control-group" *ngIf="!column.isAction">
              <app-table-cell-config-editor
                [navigationAccordion]="true"
                title="Navigation"
                [config]="column.cellConfig"
                [fields]="tmRuleFields(column)"
                (configChange)="onTmColumnCellConfigChange(column, $event)"
              ></app-table-cell-config-editor>
            </div>
            <!-- Per-column editability — ONE static label per table type so
                 the checkbox never flips its wording. Normal table: opt-IN
                 checkbox "Editable" (columns are read-only by default).
                 Editable table: opt-OUT checkbox "Read-only" (columns are
                 editable by default). -->
            <div class="control-group tm-column-editable" *ngIf="isTmEditable || isTmNormal">
              <label class="tm-editable-toggle" *ngIf="isTmNormal && !column.isAction" title="Check to let users edit this column's cells">
                <input
                  type="checkbox"
                  [ngModel]="isTmColumnEditable(column)"
                  (ngModelChange)="onTmColumnEditableChange(column, $event)"
                />
                <span>Editable</span>
              </label>
              <label class="tm-editable-toggle" *ngIf="isTmEditable && !column.isAction" title="Check to lock this column as read-only">
                <input
                  type="checkbox"
                  [ngModel]="!isTmColumnEditable(column)"
                  (ngModelChange)="onTmColumnEditableChange(column, !$event)"
                />
                <span>Read-only</span>
              </label>
              <!-- Presentation-only: a hidden column disappears from the
                   table but its value is STILL sent in the save payload. -->
              <label class="tm-editable-toggle" *ngIf="!column.isAction" title="Hide this column in the table — its value is still sent in the save payload">
                <input
                  type="checkbox"
                  [ngModel]="!!column.hidden"
                  (ngModelChange)="onTmColumnHiddenChange(column, $event)"
                />
                <span>Hidden</span>
              </label>
              <!-- Marks the column required: its header renders a * so
                   users can see which values must be filled in. -->
              <label class="tm-editable-toggle" *ngIf="!column.isAction" title="Mark this column mandatory — its header shows a * in the table">
                <input
                  type="checkbox"
                  [ngModel]="!!column.mandatory"
                  (ngModelChange)="onTmColumnMandatoryChange(column, $event)"
                />
                <span>Mandatory</span>
              </label>
              <!-- ACTION column: replaces the column's value with a button
                   in every row (see the Action block below). -->
              <label class="tm-editable-toggle" title="Turn this into an action column — every row shows a button instead of a value">
                <input
                  type="checkbox"
                  [ngModel]="!!column.isAction"
                  (ngModelChange)="onTmColumnIsActionChange(column, $event)"
                />
                <span>Action column</span>
              </label>
            </div>
            <!-- Grouped / Tree / Financial: "Hidden" on its own, so a column the
                 table only filters on ("Show only rows where") can stay out of view. -->
            <div class="control-group tm-column-editable" *ngIf="!(isTmEditable || isTmNormal) && !column.isAction">
              <label class="tm-editable-toggle" title="Hide this column in the table — it can still be filtered on">
                <input
                  type="checkbox"
                  [ngModel]="!!column.hidden"
                  (ngModelChange)="onTmColumnHiddenChange(column, $event)"
                />
                <span>Hidden</span>
              </label>
            </div>

            <!-- ── Action column settings ──────────────────────────────
                 Only shown once "Action column" is ticked. The column
                 needs no dataset field: its `field` is just the header
                 key, so nothing is read from or saved to the row. -->
            <div class="tm-column-action-box" *ngIf="column.isAction">
              <!-- Buttons in this cell (Approve / Reject / Request Info ...), each running its own
                   feature - API, popup screen, navigate, approval, export ... - for the row clicked.
                   Adding one turns the column into a buttons column; removing the last turns it back. -->
              <app-design-cell-buttons
                [buttons]="column.actionKind === 'buttons' ? (column.cellButtons ?? []) : []"
                [tableName]="host.title"
                (buttonsChange)="setTmColumnButtons(column, $event)"
              ></app-design-cell-buttons>
              <!-- Hide buttons by label, whichever config (saved by-value cases,
                   cell buttons) brings them - e.g. "Suspend". -->
              <div class="control-row">
                <div class="control-group control-group-full">
                  <label>Hide buttons</label>
                  <input
                    type="text"
                    [ngModel]="column.hiddenButtons?.join(', ') ?? ''"
                    (ngModelChange)="setTmColumnHiddenButtons(column, $event)"
                    [ngModelOptions]="{ updateOn: 'blur' }"
                    placeholder="e.g. Suspend, Request Info"
                    title="Button labels not to show in this column, comma-separated (case-insensitive)"
                  />
                </div>
              </div>
              <small class="control-hint" *ngIf="column.actionKind !== 'buttons'">
                Or one button, set up below:
              </small>
              <div class="control-row" *ngIf="column.actionKind !== 'buttons'">
                <div class="control-group">
                  <label>On click</label>
                  <ng-select
                    #ssTmActKind
                    class="ss-select"
                    [class.is-open]="!!ssTmActKind.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="tmColumnActionKinds"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="true"
                    [searchable]="false"
                    [ngModel]="column.actionKind || 'emit'"
                    (ngModelChange)="column.actionKind = $event ?? ''; onTmColumnActionChange()"
                  ></ng-select>
                </div>
                <div class="control-group">
                  <label>Icon</label>
                  <!-- Live preview beside the field: the input holds an
                       icon NAME, so without rendering it there was no way
                       to tell a valid name from a typo until you looked at
                       the table. -->
                  <div class="tm-action-icon-row">
                    <input
                      type="text"
                      [ngModel]="column.buttonIcon"
                      (ngModelChange)="column.buttonIcon = $event; onTmColumnActionChange()"
                      placeholder="visibility / history / 👁"
                      title="A Material icon name (visibility, history, edit, download) or any emoji / short glyph"
                    />
                    <span
                      class="tm-action-icon-preview"
                      [class.is-empty]="!column.buttonIcon?.trim()"
                      [title]="column.buttonIcon?.trim() ? 'Preview — this is how it renders in the table' : 'No icon set'"
                    >
                      <mat-icon *ngIf="isTmMaterialIcon(column.buttonIcon)">{{ column.buttonIcon }}</mat-icon>
                      <span *ngIf="column.buttonIcon?.trim() && !isTmMaterialIcon(column.buttonIcon)">{{ column.buttonIcon }}</span>
                      <span class="tm-action-icon-none" *ngIf="!column.buttonIcon?.trim()">—</span>
                    </span>
                  </div>
                  <small class="control-hint" *ngIf="column.buttonIcon?.trim() && isTmMaterialIcon(column.buttonIcon)">
                    If the preview shows the name as text instead of a symbol, that Material icon name doesn't exist.
                  </small>
                </div>
                <div class="control-group">
                  <label>Button text</label>
                  <input
                    type="text"
                    [ngModel]="column.buttonLabel"
                    (ngModelChange)="column.buttonLabel = $event; onTmColumnActionChange()"
                    placeholder="Optional — icon only if blank"
                  />
                </div>
              </div>

              <!-- popupComponent: point at a POPUP tile on this dashboard.
                   Its content blocks (heading / text / key-value / table /
                   apiTable / buttons) render in the row's popup, with
                   {field} filled from that row. -->
              <div class="control-row" *ngIf="column.actionKind === 'popupComponent'">
                <div class="control-group control-group-full">
                  <label>Use popup</label>
                  <ng-select
                    #ssTmActPopup
                    class="ss-select"
                    [class.is-open]="!!ssTmActPopup.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="tmPopupComponentOptions"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="true"
                    [searchable]="true"
                    [placeholder]="tmPopupComponentOptions.length ? 'Pick a popup component' : 'No popup components on this dashboard yet'"
                    [ngModel]="column.popupComponentId || null"
                    (ngModelChange)="column.popupComponentId = $event ?? ''; onTmColumnActionChange()"
                  ></ng-select>
                  <small class="control-hint is-error" *ngIf="tmPopupComponentMissing(column)">
                    ⚠ That popup is no longer on this dashboard — pick another.
                  </small>
                  <small class="control-hint" *ngIf="!tmPopupComponentOptions.length">
                    Add a Popup component to this dashboard first, then choose it here.
                  </small>
                  <small class="control-hint" *ngIf="tmPopupComponentOptions.length">
                    Build the popup's heading / text / table blocks in that Popup component.
                    Use <code>{{ '{' }}field{{ '}' }}</code> in its block text or API URLs to pull values from the clicked row.
                    Its content is snapshotted on Generate — re-generate this table after editing the popup.
                  </small>
                </div>
              </div>

              <!-- URL / method / body only matter for the API kinds. -->
              <ng-container *ngIf="tmActionNeedsUrl(column)">
                <div class="control-row">
                  <div class="control-group control-group-full">
                    <label>Action URL</label>
                    <input
                      type="text"
                      [ngModel]="column.actionUrl"
                      (ngModelChange)="column.actionUrl = $event; onTmColumnActionChange()"
                      placeholder="/cu_ref_config_rule/history"
                      title="Relative paths resolve against the platform API base, same as the other configured URLs"
                    />
                  </div>
                  <div class="control-group">
                    <label>Method</label>
                    <ng-select
                      #ssTmActMethod
                      class="ss-select"
                      [class.is-open]="!!ssTmActMethod.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="tmColumnActionMethods"
                      bindLabel="label"
                      bindValue="value"
                      [clearable]="true"
                      [searchable]="false"
                      [ngModel]="column.actionMethod || 'POST'"
                      (ngModelChange)="column.actionMethod = $event ?? ''; onTmColumnActionChange()"
                    ></ng-select>
                  </div>
                </div>
                <div class="control-row">
                  <div class="control-group control-group-full">
                    <label>Request body (JSON)</label>
                    <textarea
                      class="tm-action-body-editor"
                      rows="5"
                      spellcheck="false"
                      [class.is-invalid]="tmActionBodyError(column)"
                      [ngModel]="column.actionBody"
                      (ngModelChange)="column.actionBody = $event; onTmColumnActionChange()"
                      placeholder='Blank sends the whole row. Example:&#10;{&#10;  "id": "{rule_name}",&#10;  "premium": {incremental_premium}&#10;}'
                    ></textarea>
                    <!-- Live JSON check on the template (placeholders are
                         stubbed first, so a valid template never reads as
                         broken just because it has {tokens} in it). -->
                    <small class="control-hint is-error" *ngIf="tmActionBodyError(column)">
                      ⚠ {{ tmActionBodyError(column) }}
                    </small>
                    <small class="control-hint" *ngIf="!tmActionBodyError(column) && column.actionBody?.trim()">
                      ✓ Valid JSON
                    </small>
                    <small class="control-hint">
                      <strong>Dynamic values:</strong> wrap a column's field name in braces.
                      <code>"{{ '{' }}field{{ '}' }}"</code> (in quotes) sends it as text;
                      <code>{{ '{' }}field{{ '}' }}</code> (no quotes) keeps its real type — number, true/false, null.
                    </small>
                    <!-- The exact tokens this table can substitute, so the
                         author doesn't have to guess field names. -->
                    <small class="control-hint" *ngIf="tmActionTokens().length">
                      <strong>Available:</strong>
                      <button
                        *ngFor="let token of tmActionTokens()"
                        type="button"
                        class="tm-action-token"
                        (click)="appendTmActionToken(column, token)"
                        [title]="'Insert ' + token"
                      >{{ token }}</button>
                    </small>
                  </div>
                </div>
                <div class="control-row">
                  <div class="control-group" *ngIf="column.actionKind === 'apiPopup'">
                    <label>Response rows path</label>
                    <input
                      type="text"
                      [ngModel]="column.actionRowsPath"
                      (ngModelChange)="column.actionRowsPath = $event; onTmColumnActionChange()"
                      placeholder="history — blank = auto-detect"
                      title="Dot-path to the rows array. A single-element array wrapper is stepped through automatically, so 'history' works for [{ history: [...] }] too."
                    />
                  </div>
                  <!-- Sort + cap: this is how "show only the latest
                       version" is expressed — sort by the version/date
                       field descending and keep 1 row. -->
                  <div class="control-group" *ngIf="column.actionKind === 'apiPopup'">
                    <label>Sort rows by</label>
                    <input
                      type="text"
                      [ngModel]="column.actionRowsSortBy"
                      (ngModelChange)="column.actionRowsSortBy = $event; onTmColumnActionChange()"
                      placeholder="Version_no / Updated_date"
                      title="Field in the response to sort by. Nested fields also match by their short name."
                    />
                  </div>
                  <div class="control-group" *ngIf="column.actionKind === 'apiPopup' && column.actionRowsSortBy?.trim()">
                    <label>Direction</label>
                    <ng-select
                      #ssTmActSortDir
                      class="ss-select"
                      [class.is-open]="!!ssTmActSortDir.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="tmActionSortDirections"
                      bindLabel="label"
                      bindValue="value"
                      [clearable]="true"
                      [searchable]="false"
                      [ngModel]="column.actionRowsSortDir || 'desc'"
                      (ngModelChange)="column.actionRowsSortDir = $event ?? ''; onTmColumnActionChange()"
                    ></ng-select>
                  </div>
                  <!-- Pick + order the response columns. A history/audit
                       response usually carries far more fields than are
                       worth showing. -->
                  <div class="control-group control-group-full" *ngIf="column.actionKind === 'apiPopup'">
                    <label>Show columns</label>
                    <input
                      type="text"
                      [ngModel]="column.actionRowsColumns"
                      (ngModelChange)="column.actionRowsColumns = $event; onTmColumnActionChange()"
                      placeholder="Version_no, Updated_by, comment — blank = all"
                      title="Comma-separated. Each entry matches the response field by its short name or full path. Use name:Label to rename a header."
                    />
                    <small class="control-hint">
                      In order, comma-separated. Short names work
                      (<code>comment</code> matches <code>DATA.comment</code>), and
                      <code>name:Label</code> renames the header — e.g.
                      <code>Version_no:Version, DATA.incremental_premium:Premium</code>.
                      Blank shows every column.
                    </small>
                  </div>
                  <div class="control-group" *ngIf="column.actionKind === 'apiPopup'">
                    <label>Max rows</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      [ngModel]="column.actionMaxRows"
                      (ngModelChange)="column.actionMaxRows = $event; onTmColumnActionChange()"
                      placeholder="0 = all"
                      title="Show at most this many rows after sorting. Set 1 with a descending sort to show only the latest."
                    />
                  </div>
                  <div class="control-group tm-column-editable" *ngIf="column.actionKind === 'api' || column.actionKind === 'apiPopup'">
                    <label class="tm-editable-toggle" title="Re-fetch the table after a successful call — for actions that change the row">
                      <input
                        type="checkbox"
                        [ngModel]="!!column.actionRefresh"
                        (ngModelChange)="column.actionRefresh = $event; onTmColumnActionChange()"
                      />
                      <span>Refresh table after call</span>
                    </label>
                  </div>
                </div>
              </ng-container>

              <div class="control-row">
                <div class="control-group" *ngIf="column.actionKind === 'rowPopup' || column.actionKind === 'apiPopup'">
                  <label>Popup title</label>
                  <input
                    type="text"
                    [ngModel]="column.actionTitle"
                    (ngModelChange)="column.actionTitle = $event; onTmColumnActionChange()"
                    [placeholder]="column.displayName || 'Row details'"
                  />
                </div>
                <div class="control-group control-group-full">
                  <label>Confirm before running</label>
                  <input
                    type="text"
                    [ngModel]="column.actionConfirm"
                    (ngModelChange)="column.actionConfirm = $event; onTmColumnActionChange()"
                    placeholder="Blank = no confirmation. e.g. Approve this rule?"
                  />
                </div>
              </div>

              <!-- Buttons by row value (actionByValue): a row's buttons are picked by another
                   column's value - e.g. Approve / Reject on WAITING_FOR_APPROVAL rows, a dash on
                   the rest. Each button has its own label, colour and action, and can be removed. -->
              <div class="tm-abv" *ngIf="column.actionKind !== 'buttons' && column.actionKind !== 'popupComponent'">
                <label class="tm-editable-toggle" title="Show different buttons depending on a column's value in each row">
                  <input
                    type="checkbox"
                    [ngModel]="!!column.actionByValue"
                    (ngModelChange)="setTmActionByValueEnabled(column, $event)"
                  />
                  <span>Different buttons per row value</span>
                </label>
                <ng-container *ngIf="column.actionByValue as byValue">
                  <small class="control-hint">
                    Each row shows the buttons listed under its value (matched ignoring case).
                    Click a button to edit it; the bin icon removes it.
                  </small>
                  <div class="control-group">
                    <label>Row value from</label>
                    <ng-select
                      #ssTmAbvField
                      class="ss-select"
                      [class.is-open]="!!ssTmAbvField.isOpen?.()"
                      appendTo="body"
                      ngClass="ss-dropdown-panel"
                      [items]="tmActionValueFieldOptions()"
                      bindLabel="label"
                      bindValue="value"
                      [addTag]="true"
                      addTagText="Use field"
                      [clearable]="true"
                      [searchable]="true"
                      placeholder="Pick a column, or type a field name"
                      [ngModel]="byValue.field || null"
                      (ngModelChange)="setTmActionByValueField(column, $event)"
                    ></ng-select>
                  </div>

                  <div class="tm-abv-case" *ngFor="let entry of tmActionCases(column); trackBy: trackByTmActionCase">
                    <div class="tm-abv-case-head">
                      <span class="tm-abv-when">When value is</span>
                      <input
                        type="text"
                        class="tm-abv-value"
                        [value]="entry.value"
                        (change)="renameTmActionCase(column, entry.value, $any($event.target).value)"
                        placeholder="e.g. WAITING_FOR_APPROVAL"
                        title="The row value these buttons are for"
                      />
                      <button type="button" class="tm-abv-icon-btn" title="Remove this value and its buttons"
                        (click)="removeTmActionCase(column, entry.value)">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                    <ng-container *ngTemplateOutlet="tmAbvButtons; context: { $implicit: entry.actionCase, column: column }"></ng-container>
                  </div>
                  <button type="button" class="tm-abv-add" (click)="addTmActionCase(column)">+ Add value</button>

                  <div class="tm-abv-case is-default" *ngIf="byValue.default as otherCase; else tmAbvNoDefault">
                    <div class="tm-abv-case-head">
                      <span class="tm-abv-when">Any other value</span>
                      <button type="button" class="tm-abv-icon-btn"
                        title="Remove - rows with any other value then show the single button set up above"
                        (click)="removeTmActionDefault(column)">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                    <ng-container *ngTemplateOutlet="tmAbvButtons; context: { $implicit: otherCase, column: column }"></ng-container>
                  </div>
                  <ng-template #tmAbvNoDefault>
                    <small class="control-hint">
                      Rows with any other value show the single button set up above.
                      <button type="button" class="tm-abv-link" (click)="addTmActionDefault(column)">Set their buttons</button>
                    </small>
                  </ng-template>
                </ng-container>
              </div>
            </div>
            <div class="control-group" *ngIf="(isTmEditable || isTmNormal) && isTmColumnEditable(column) && !column.isAction">
              <label>Input type</label>
              <ng-select
                #ssTmColControl
                class="ss-select"
                [class.is-open]="!!ssTmColControl.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="tmColumnControls"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [searchable]="true"
                placeholder="Text box"
                [ngModel]="column.control || 'text'"
                (ngModelChange)="onTmColumnControlChange(column, $event)"
              ></ng-select>
            </div>
            <div class="control-group" *ngIf="(isTmEditable || isTmNormal) && isTmColumnEditable(column) && !column.isAction">
              <label class="tm-editable-toggle" title="Show Save all only when this column was edited">
                <input
                  type="checkbox"
                  [ngModel]="!!column.saveAllTrigger"
                  (ngModelChange)="onTmColumnSaveAllTriggerChange(column, $event)"
                />
                <span>Show Save all on edit</span>
              </label>
            </div>
            <div class="control-group" *ngIf="!column.isAction">
              <label>Number format</label>
              <ng-select
                #ssTmColType
                class="ss-select"
                [class.is-open]="!!ssTmColType.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="tmColumnNumberTypes"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [searchable]="false"
                placeholder="Auto"
                [ngModel]="column.type || null"
                (ngModelChange)="onTmColumnTypeChange(column, $event)"
              ></ng-select>
            </div>
            <div class="control-group" *ngIf="!column.isAction && column.type">
              <label>Decimals</label>
              <input
                type="number"
                min="0"
                max="6"
                [ngModel]="column.decimals ?? null"
                (ngModelChange)="onTmColumnDecimalsChange(column, $event)"
                placeholder="Table default"
              />
            </div>
            <div class="control-group">
              <label>Alignment</label>
              <ng-select
                #ssTmColAlign
                class="ss-select"
                [class.is-open]="!!ssTmColAlign.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="tmColumnAlignments"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [searchable]="false"
                placeholder="Default"
                [ngModel]="column.align || null"
                (ngModelChange)="onTmColumnAlignChange(column, $event)"
              ></ng-select>
            </div>
            <!-- GENERIC cell configuration — the ONE editor shared with the
                 Drill-Down measures / dimensions (table-cell-config.ts):
                 display kind, colours, conditional rules, highlight, prefix /
                 suffix / tooltip, and the dropdown values when the column's
                 input type is a select. -->
            <!-- Money column: follows the Currency exchange dropdown's rate (display only). -->
            <label class="tm-editable-toggle" *ngIf="!column.isAction"
              title="Shown × the rate selected in the Currency exchange dropdown, with its symbol. Display only.">
              <input type="checkbox" [ngModel]="!!column.cellConfig?.convertCurrency" (ngModelChange)="setTmColumnCurrency(column, $event)" />
              <span>Convert currency (follows the exchange rate)</span>
            </label>
            <app-table-cell-config-editor
              *ngIf="!column.isAction"
              title="Cell formatting"
              [showNavigation]="false"
              [config]="column.cellConfig"
              [fields]="tmRuleFields(column)"
              [values]="tmColumnValues(column)"
              [loadValues]="tmValuesLoader(column)"
              [showOptions]="tmColumnListsOptions(column)"
              valueKind="text"
              (configChange)="onTmColumnCellConfigChange(column, $event)"
            ></app-table-cell-config-editor>
          </div>

          <!-- Default filter attached to THIS column's row — applied on the
               table's FIRST data request, before any global filter. "Filter
               column" defaults to this row's own column but can be pointed
               at ANY other column (e.g. attach a rule_categories filter to
               the Rule Name row even though rule_categories isn't itself
               displayed). Blank value = no filter for this row. -->
          <div class="tm-column-card-body tm-column-filter-row">
            <div class="control-group">
              <label>Filter column</label>
              <ng-select
                #ssTmColFilterField
                class="ss-select"
                [class.is-open]="!!ssTmColFilterField.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="columnsForTmColumn(column)"
                [clearable]="true"
                [searchable]="true"
                [loading]="isTmColumnLoading(column)"
                [placeholder]="column.field || 'Same as this column'"
                [ngModel]="column.filterField || null"
                (ngModelChange)="onTmColumnFilterFieldChange(column, $event)"
              ></ng-select>
            </div>
            <div class="control-group">
              <label>Operator</label>
              <ng-select
                #ssTmColFilterOp
                class="ss-select"
                [class.is-open]="!!ssTmColFilterOp.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="tmInitialFilterOperators"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="column.filterOperator"
                (ngModelChange)="onTmColumnFilterOperatorChange(column, $event)"
              ></ng-select>
            </div>
            <div class="control-group">
              <label>Value</label>
              <input
                type="text"
                [ngModel]="column.filterValue"
                (ngModelChange)="onTmColumnFilterValueChange(column, $event)"
                placeholder="Leave blank for no default filter"
              />
            </div>
          </div>
        </div>
      </div>

      <button type="button" mat-stroked-button class="add-step-button tm-add-column" (click)="addTmColumn(toggle)"><mat-icon>add</mat-icon> Add column</button>
    </div>
    </ng-template>

    <!-- View 1 (the table's normal columns). -->
    <ng-container
      *ngIf="isTmNormal || isTmEditable || isTmGrouped || isTmTreegrid || isTmFinancial"
      [ngTemplateOutlet]="tmColumnCards"
      [ngTemplateOutletContext]="{
        columns: tmColumns,
        toggle: false,
        label: host.fieldLabel('tableMatrix', 'columns', 'Columns'),
        hint: 'No columns picked — the table shows every column of the primary dataset. Add rows to pick specific columns (from any dataset) and rename their headers.'
      }"
    ></ng-container>

    <!-- ══ Header view toggle: ONE table, two configured views ══
         Option 1 is the table exactly as configured above. Option 2 gets
         its own dataset + columns, fetched with its own compute payload
         when the user flips the toggle in the table header. -->
    <div class="control-group tm-view-toggle-config" *ngIf="isTmNormal || isTmEditable || isTmGrouped || isTmTreegrid || isTmFinancial">
      <label class="tm-editable-toggle" title="Show a two-option toggle in the table header that swaps the columns and the dataset">
        <input type="checkbox" [ngModel]="tmToggleEnabled" (ngModelChange)="tmToggleEnabled = $event; onTableMatrixTypeChange()" />
        <span>Enable header view toggle</span>
      </label>
      <small class="control-hint">
        Adds a two-option switch to the table header. The first option shows the columns configured above; the second shows its own dataset and columns, requested with its own payload when picked.
      </small>

      <ng-container *ngIf="tmToggleEnabled">
        <div class="control-row">
          <div class="control-group">
            <label>Option 1 label</label>
            <input type="text" placeholder="View 1" [ngModel]="tmToggleLabel1" (ngModelChange)="tmToggleLabel1 = $event; onTableMatrixTypeChange()" />
          </div>
          <div class="control-group">
            <label>Option 2 label</label>
            <input type="text" placeholder="View 2" [ngModel]="tmToggleLabel2" (ngModelChange)="tmToggleLabel2 = $event; onTableMatrixTypeChange()" />
          </div>
        </div>

        <div class="control-group">
          <label>Option 2 — dataset</label>
          <ng-select
            #ssTmToggleDataset
            class="ss-select"
            [class.is-open]="!!ssTmToggleDataset.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="host.entityOptions"
            [loading]="host.entitiesLoading"
            [clearable]="true"
            [searchable]="true"
            placeholder="Same as primary"
            [ngModel]="tmToggleDataset || null"
            (ngModelChange)="onTmToggleDatasetChange($event)"
          ></ng-select>
        </div>

        <!-- Option 2's columns get the SAME per-column configuration as
             the table's normal columns. -->
        <ng-container
          [ngTemplateOutlet]="tmColumnCards"
          [ngTemplateOutletContext]="{
            columns: tmToggleColumns,
            toggle: true,
            label: 'Option 2 — columns',
            hint: 'No columns picked — add rows to choose the columns this view shows. These are the columns sent in the request when option 2 is selected.'
          }"
        ></ng-container>
      </ng-container>
    </div>

    <div class="control-row" *ngIf="isTmNormal">
      <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onTmGroupByFieldDrop($event)" title="Drag a column here to group rows">
        <label>{{ host.fieldLabel('tableMatrix', 'groupBy', 'Group rows by') }}</label>
        <ng-select
          #ssTmGroup
          class="ss-select"
          [class.is-open]="!!ssTmGroup.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="host.drillDatasetColumns"
          [clearable]="true"
          [searchable]="true"
          [loading]="host.isDrillDatasetColumnsLoading"
          [disabled]="!host.drillDataset"
          placeholder="No grouping"
          [ngModel]="tmGroupBy || null"
          (ngModelChange)="tmGroupBy = $event || ''; onTableMatrixTypeChange()"
        ></ng-select>
      </div>
      <div class="control-group">
        <label>{{ host.fieldLabel('tableMatrix', 'aggregation', 'Aggregation') }}</label>
        <ng-select
          #ssTmAgg
          class="ss-select"
          [class.is-open]="!!ssTmAgg.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="tmAggregations"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="tmAggregation"
          (ngModelChange)="tmAggregation = $event ?? ''; onTableMatrixTypeChange()"
        ></ng-select>
      </div>
    </div>

    <!-- ══ Matrix: row dimension × column dimension × measure ══ -->
    <ng-container *ngIf="isTmMatrix">
      <div class="control-row">
        <div class="control-group">
          <label>Row dimension</label>
          <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Rows down" [ngModel]="tmRowField || null" (ngModelChange)="tmRowField = $event || ''; onTableMatrixTypeChange()"></ng-select>
        </div>
        <div class="control-group">
          <label>Column dimension</label>
          <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Columns across" [ngModel]="tmColumnField || null" (ngModelChange)="tmColumnField = $event || ''; onTableMatrixTypeChange()"></ng-select>
        </div>
      </div>
      <div class="control-group">
        <label>Value (measure)</label>
        <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Numeric column to aggregate" [ngModel]="tmValueField || null" (ngModelChange)="tmValueField = $event || ''; onTableMatrixTypeChange()"></ng-select>
      </div>
    </ng-container>

    <!-- ══ Pivot: row fields × column fields × measure ══ -->
    <ng-container *ngIf="isTmPivot">
      <div class="control-group">
        <label>Row fields</label>
        <ng-select class="ss-select ss-select--multi" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [multiple]="true" [closeOnSelect]="false" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Rows (one or more)" [ngModel]="tmRowFields" (ngModelChange)="tmRowFields = $event || []; onTableMatrixTypeChange()"></ng-select>
      </div>
      <div class="control-group">
        <label>Column fields</label>
        <ng-select class="ss-select ss-select--multi" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [multiple]="true" [closeOnSelect]="false" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Columns (one or more)" [ngModel]="tmColumnFields2" (ngModelChange)="tmColumnFields2 = $event || []; onTableMatrixTypeChange()"></ng-select>
      </div>
      <div class="control-group">
        <label>Values (measures — each fans out per column value)</label>
        <ng-select class="ss-select ss-select--multi" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [multiple]="true" [closeOnSelect]="false" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Numeric column(s) to aggregate" [ngModel]="tmValueFields" (ngModelChange)="tmValueFields = $event || []; onTableMatrixTypeChange()"></ng-select>
        <small class="control-hint">Pick several to get Excel-style year blocks: e.g. Column field = fiscal_year, Values = net_quantity + net_sales → 2024 Net Quantity | 2025 … | 2024 Net Sales | 2025 …</small>
      </div>
    </ng-container>

    <!-- ══ Grouped: ordered group levels ══ -->
    <div class="control-group" *ngIf="isTmGrouped">
      <label>Group by (levels, outer → inner)</label>
      <ng-select class="ss-select ss-select--multi" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [multiple]="true" [closeOnSelect]="false" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Pick group columns in order" [ngModel]="tmGroupByFields" (ngModelChange)="tmGroupByFields = $event || []; onTableMatrixTypeChange()"></ng-select>
    </div>

    <!-- ══ Tree Grid: id + parent-id columns ══ -->
    <div class="control-row" *ngIf="isTmTreegrid">
      <div class="control-group">
        <label>Row ID column</label>
        <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Unique id" [ngModel]="tmTreeIdField || null" (ngModelChange)="tmTreeIdField = $event || ''; onTableMatrixTypeChange()"></ng-select>
      </div>
      <div class="control-group">
        <label>Parent ID column</label>
        <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Points to parent id" [ngModel]="tmTreeParentField || null" (ngModelChange)="tmTreeParentField = $event || ''; onTableMatrixTypeChange()"></ng-select>
      </div>
    </div>

    <!-- ══ Financial: role + indent columns ══ -->
    <div class="control-row" *ngIf="isTmFinancial">
      <div class="control-group">
        <label>Row role column (header/item/subtotal/total)</label>
        <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Optional role column" [ngModel]="tmRoleField || null" (ngModelChange)="tmRoleField = $event || ''; onTableMatrixTypeChange()"></ng-select>
      </div>
      <div class="control-group">
        <label>Indent column (optional)</label>
        <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.drillDatasetColumns" [clearable]="true" [searchable]="true" [disabled]="!host.drillDataset" placeholder="Numeric indent depth" [ngModel]="tmIndentField || null" (ngModelChange)="tmIndentField = $event || ''; onTableMatrixTypeChange()"></ng-select>
      </div>
    </div>

    <!-- ══ Navigation: click a cell value → open a target dashboard filtered ══ -->
    <div class="tm-nav-config">
      <div class="tm-nav-head" (click)="toggleTmNavAccordion()">
        <mat-icon>{{ tmNavAccordionOpen ? 'expand_more' : 'chevron_right' }}</mat-icon>
        <span>Navigation</span>
        <span class="tm-nav-badge" *ngIf="tmNavEnabled">on</span>
      </div>
      <div class="tm-nav-body" *ngIf="tmNavAccordionOpen">
        <label class="dvm-format-toggle tm-nav-enable">
          <input type="checkbox" [ngModel]="tmNavEnabled" (ngModelChange)="tmNavEnabled = $event; onTableMatrixTypeChange()" />
          Enable dashboard navigation
        </label>

        <ng-container *ngIf="tmNavEnabled">
          <div class="control-group">
            <label>Filter column (value sent on navigate)</label>
            <ng-select
              #ssTmNavKey
              class="ss-select"
              [class.is-open]="!!ssTmNavKey.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="host.drillDatasetColumns"
              [clearable]="true"
              [searchable]="true"
              [disabled]="!host.drillDataset"
              placeholder="Defaults to the first column"
              [ngModel]="tmNavFilterKey || null"
              (ngModelChange)="tmNavFilterKey = $event || ''; onTableMatrixTypeChange()"
            ></ng-select>
          </div>

          <div class="control-group">
            <label>Clickable columns (empty = filter column only)</label>
            <ng-select
              #ssTmNavCols
              class="ss-select ss-select--multi"
              [class.is-open]="!!ssTmNavCols.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="host.drillDatasetColumns"
              [multiple]="true"
              [closeOnSelect]="false"
              [hideSelected]="false"
              [clearable]="true"
              [searchable]="true"
              [disabled]="!host.drillDataset"
              placeholder="Filter column only"
              [ngModel]="tmNavColumns"
              (ngModelChange)="tmNavColumns = $event || []; onTableMatrixTypeChange()"
            ></ng-select>
          </div>

          <div class="control-group">
            <label>Target dashboard</label>
            <ng-select
              #ssTmNavDash
              class="ss-select"
              [class.is-open]="!!ssTmNavDash.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="host.tmNavTargetDashboards"
              bindLabel="label"
              bindValue="value"
              [clearable]="true"
              [searchable]="true"
              placeholder="Pick a saved dashboard"
              [ngModel]="tmNavTargetDashboard || null"
              (ngModelChange)="tmNavTargetDashboard = $event || ''; onTmNavTargetDashboardChange()"
            ></ng-select>
            <small class="control-hint" *ngIf="!host.tmNavTargetDashboards.length">No saved dashboards yet — publish/save one to use as a target.</small>
          </div>

          <div class="control-group" *ngIf="tmNavTargetDashboard">
            <label>Target components (empty = all charts)</label>
            <ng-select
              #ssTmNavComps
              class="ss-select ss-select--multi"
              [class.is-open]="!!ssTmNavComps.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="tmNavTargetComponentOptions"
              bindLabel="label"
              bindValue="value"
              [multiple]="true"
              [closeOnSelect]="false"
              [hideSelected]="false"
              [clearable]="true"
              [searchable]="true"
              placeholder="All charts on the target dashboard"
              [ngModel]="tmNavTargetComponents"
              (ngModelChange)="tmNavTargetComponents = $event || []; onTableMatrixTypeChange()"
            ></ng-select>
          </div>

          <div class="control-group">
            <label>Navigate button label</label>
            <input type="text" [ngModel]="tmNavButtonLabel" (ngModelChange)="tmNavButtonLabel = $event; onTableMatrixTypeChange()" placeholder="Navigate" />
          </div>
        </ng-container>
      </div>
    </div>
  </ng-container>

  <!-- Table format options — the SAME config-driven format accordions as
       the Bar/Waterfall layouts (Formatting / Totals / Columns /
       Interaction / Export / Upload), fed by the TABLE_MATRIX-scoped
       formatControls in charts/table-matrix.json, gated per table type by
       `visibleWhenType` and by the capabilityRegistry `sections` list;
       the values fold into the tile on Generate and live-refresh the
       edited tile. For the DRILL-DOWN type these accordions render AFTER
       the Hierarchy dimensions / Measures / Chart-interactions block (see
       the second outlet lower down) so the required mapping comes first. -->
  <ng-container *ngIf="!isTmDrilldown" [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
</div>

<!-- One row value's buttons in the "Different buttons per row value" editor: each button as a
     chip (how it looks in the table) with edit / remove, its settings below when opened. A
     setting left blank falls back to the action column's own. -->
<ng-template #tmAbvButtons let-actionCase let-column="column">
  <div class="tm-abv-buttons">
    <span class="tm-abv-none" *ngIf="!tmCaseButtons(actionCase).length">— no button on these rows</span>
    <div class="tm-abv-button" *ngFor="let button of tmCaseButtons(actionCase); let i = index">
      <div class="tm-abv-button-head">
        <button type="button" class="tm-abv-chip" [attr.data-tone]="button.tone || ''" [class.is-link]="button.style === 'link'"
          [title]="isTmCaseButtonOpen(button) ? 'Close' : 'Edit this button'" (click)="toggleTmCaseButton(button)">
          {{ button.label || column.buttonLabel || 'Button' }}
        </button>
        <button type="button" class="tm-abv-icon-btn" [title]="isTmCaseButtonOpen(button) ? 'Close' : 'Edit'"
          (click)="toggleTmCaseButton(button)">
          <mat-icon>{{ isTmCaseButtonOpen(button) ? 'expand_less' : 'edit' }}</mat-icon>
        </button>
        <button type="button" class="tm-abv-icon-btn is-danger" title="Remove this button"
          (click)="removeTmCaseButton(actionCase, i)">
          <mat-icon>delete</mat-icon>
        </button>
      </div>
      <div class="tm-abv-button-body" *ngIf="isTmCaseButtonOpen(button)">
        <div class="control-row">
          <div class="control-group">
            <label>Text</label>
            <input type="text" [ngModel]="button.label" (ngModelChange)="setTmCaseButtonValue(button, 'label', $event)"
              [placeholder]="column.buttonLabel || 'e.g. Approve'" />
          </div>
          <div class="control-group">
            <label>Style</label>
            <ng-select #ssTmAbvStyle class="ss-select" [class.is-open]="!!ssTmAbvStyle.isOpen?.()" appendTo="body"
              ngClass="ss-dropdown-panel" [items]="tmActionCaseStyles" bindLabel="label" bindValue="value"
              [clearable]="false" [searchable]="false" [ngModel]="button.style || 'button'"
              (ngModelChange)="setTmCaseButtonValue(button, 'style', $event)"></ng-select>
          </div>
          <div class="control-group">
            <label>Colour</label>
            <ng-select #ssTmAbvTone class="ss-select" [class.is-open]="!!ssTmAbvTone.isOpen?.()" appendTo="body"
              ngClass="ss-dropdown-panel" [items]="tmActionCaseTones" bindLabel="label" bindValue="value"
              [clearable]="false" [searchable]="false" [ngModel]="button.tone || ''"
              (ngModelChange)="setTmCaseButtonValue(button, 'tone', $event)"></ng-select>
          </div>
        </div>
        <div class="control-group">
          <label>On click</label>
          <ng-select #ssTmAbvKind class="ss-select" [class.is-open]="!!ssTmAbvKind.isOpen?.()" appendTo="body"
            ngClass="ss-dropdown-panel" [items]="tmActionCaseKinds" bindLabel="label" bindValue="value"
            [clearable]="false" [searchable]="false" [ngModel]="button.actionKind || ''"
            (ngModelChange)="setTmCaseButtonValue(button, 'actionKind', $event)"></ng-select>
        </div>
        <!-- Button feature: the same settings as a Button widget (and the column's cell buttons). -->
        <app-design-cell-button-feature *ngIf="button.actionKind === 'button'"
          [config]="button.config ?? {}"
          [label]="button.label || column.buttonLabel || ''"
          [tableName]="host.title"
          (configChange)="patchTmCaseButtonConfig(button, $event)"
        ></app-design-cell-button-feature>
        <ng-container *ngIf="tmCaseButtonCallsApi(button, column)">
          <div class="control-row">
            <div class="control-group control-group-full">
              <label>Action URL</label>
              <input type="text" [ngModel]="button.actionUrl" (ngModelChange)="setTmCaseButtonValue(button, 'actionUrl', $event)"
                [placeholder]="column.actionUrl || '/spriced/platform/...'" />
            </div>
            <div class="control-group">
              <label>Method</label>
              <ng-select #ssTmAbvMethod class="ss-select" [class.is-open]="!!ssTmAbvMethod.isOpen?.()" appendTo="body"
                ngClass="ss-dropdown-panel" [items]="tmColumnActionMethods" bindLabel="label" bindValue="value"
                [clearable]="true" [searchable]="false" [ngModel]="button.actionMethod || null"
                (ngModelChange)="setTmCaseButtonValue(button, 'actionMethod', $event)"></ng-select>
            </div>
          </div>
          <div class="control-group control-group-full">
            <label>Request body (JSON)</label>
            <textarea class="tm-action-body-editor" rows="5" spellcheck="false"
              [class.is-invalid]="tmCaseButtonBodyError(button)"
              [ngModel]="button.actionBody" (ngModelChange)="setTmCaseButtonValue(button, 'actionBody', $event)"
              placeholder='Blank = the column&#39;s body. {field} fills in a value from the row.'></textarea>
            <small class="control-hint is-error" *ngIf="tmCaseButtonBodyError(button)">⚠ {{ tmCaseButtonBodyError(button) }}</small>
          </div>
          <label class="tm-editable-toggle" title="Re-fetch the table after a successful call">
            <input type="checkbox" [ngModel]="!!button.actionRefresh"
              (ngModelChange)="setTmCaseButtonValue(button, 'actionRefresh', $event)" />
            <span>Refresh table after call</span>
          </label>
        </ng-container>
        <div class="control-group control-group-full">
          <label>Confirm before running</label>
          <input type="text" [ngModel]="button.actionConfirm" (ngModelChange)="setTmCaseButtonValue(button, 'actionConfirm', $event)"
            placeholder="Blank = no confirmation. e.g. Approve this price?" />
        </div>
      </div>
    </div>
    <button type="button" class="tm-abv-add" (click)="addTmCaseButton(actionCase)">+ Add button</button>
  </div>
</ng-template>
